// src/v2/hooks/useDrawIn.js
//
// One-shot left-to-right "draw-in" hook for the Leases Timeline notice→expiry
// bars. Returns a 0→1 progress value that animates once per mount with an
// ease-out curve and a per-row cascade stagger of `index * STAGGER` ms,
// mirroring the pattern used by `useCountUp` for KPI tiles.
//
// Because the effect has an empty dependency array, the animation fires only
// on mount — re-renders, filter changes, and sort re-flows of an already
// mounted row never replay it. Expanding a collapsed group mounts fresh row
// instances, so those rows naturally get their own draw-in.
//
// Respects `prefers-reduced-motion: reduce` — returns 1 (final state)
// immediately when the system accessibility setting is enabled or
// matchMedia is unavailable.

import { useEffect, useRef, useState } from "react";

const DURATION = 550;
const STAGGER  = 35;

function easeOut(t) {
  return 1 - Math.pow(1 - t, 3);
}

function prefersReducedMotion() {
  // No matchMedia → skip animation (JSDOM / SSR / legacy browsers).
  if (typeof window === "undefined" || !window.matchMedia) return true;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * One-shot draw-in progress hook.
 *
 * @param {number} [index=0] Row index within its group, for a top-to-bottom stagger.
 * @param {object} [options] Mount-time duration, stagger and easing overrides;
 *   observeReducedMotion also finishes immediately on a live preference change.
 *   Omitted options preserve the legacy phased-track timing and curve.
 * @returns {number} Eased progress from 0 (not drawn) to 1 (fully drawn).
 */
export function useDrawIn(index = 0, options = {}) {
  const duration = options.duration ?? DURATION;
  const stagger = options.stagger ?? STAGGER;
  const easing = options.easing ?? easeOut;
  const [progress, setProgress] = useState(() => (prefersReducedMotion() ? 1 : 0));
  const rafRef   = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const cancelPending = () => {
      cancelled = true;
      if (timerRef.current != null) clearTimeout(timerRef.current);
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
    const media = options.observeReducedMotion && window.matchMedia
      ? window.matchMedia("(prefers-reduced-motion: reduce)")
      : null;
    const onMotionChange = (event) => {
      if (!event.matches) return;
      cancelPending();
      setProgress(1);
    };
    media?.addEventListener?.("change", onMotionChange);
    if (prefersReducedMotion()) {
      setProgress(1);
      return () => media?.removeEventListener?.("change", onMotionChange);
    }

    timerRef.current = setTimeout(() => {
      let startTs = null;

      function step(ts) {
        if (cancelled) return;
        if (startTs == null) startTs = ts;
        const t = Math.min((ts - startTs) / duration, 1);
        setProgress(easing(t));
        if (t < 1) {
          rafRef.current = requestAnimationFrame(step);
        }
      }

      rafRef.current = requestAnimationFrame(step);
    }, index * stagger);

    return () => {
      cancelPending();
      media?.removeEventListener?.("change", onMotionChange);
    };
    // Intentional: empty deps so this fires once on mount only, not on every
    // index/prop change — see file header comment for rationale.
  }, []);

  return progress;
}
